import { useCallback, useEffect, useMemo } from "react";
import currency from "currency.js";
import { getContractPriceStep } from "../../../utils/instruments";
import { calculateFarmBoostMultiplier } from "../../../utils/math";
import { ITradeInfo } from "../../../interfaces/TradeInfo";
import { BoostTitle, BoostValueWrapper } from "../style";
import BoostedVolumeTooltip from "../../shared/BoostedVolumeTooltip";
import { OrderTypeResponse } from "../../../codegen-api";
import { TpslRoeRef } from "../../../hooks/tpsl/useOrderFormTpsl";
import { PerpFormFieldKeyEnum } from "./form";
import {
  sizeBalanceUnknownReason,
  sizeWithinCap,
  usePerpsTradeFormMax,
} from "./usePerpsTradeFormMax";
import { usePerpSizePercent } from "./usePerpSizePercent";

export function usePerpsTradeFormSizing(
  ctx: ReturnType<typeof usePerpsTradeFormMax>
) {
  const {
    commonFormTranslations,
    requiredMarginWithFee,
    perpInstrument,
    notEnoughBalanceError,
    orderNotional,
    orderValueTooSmall,
    calculateTotalValueWithSize,
    amount,
    farmData,
    isSpot,
    orderDirection,
    getRealContractSize,
    tradeUseUSDCTerms,
    currentPosition,
    setValue,
    trigger,
    sizeMax,
    sizeCap,
    sizeStep,
    selectedLeverage,
    orderType,
    price,
    markPrice,
    contractPriceStep,
  } = ctx;

  const tradeInfo = useMemo<ITradeInfo[]>(() => {
    // ================ MARKET ORDERS ================
    // MARKET BUY/SELL ORDER
    return [
      {
        title: commonFormTranslations("required_margin"),
        value:
          requiredMarginWithFee !== undefined
            ? currency(requiredMarginWithFee, {
                precision: getContractPriceStep(perpInstrument).price_precision,
              }).format()
            : "---",
        warningOrError: notEnoughBalanceError ? "error" : undefined,
      },
      {
        title: commonFormTranslations("order_value"),
        value: orderNotional
          ? currency(orderNotional, {
              precision: getContractPriceStep(perpInstrument).price_precision,
            }).format()
          : "---",
        warningOrError:
          notEnoughBalanceError || orderValueTooSmall ? "error" : undefined,
      },
    ];
  }, [
    orderNotional,
    requiredMarginWithFee,
    commonFormTranslations,
    perpInstrument,
    notEnoughBalanceError,
    orderValueTooSmall,
  ]);

  const bottomTradeInfo = useMemo<ITradeInfo[]>(() => {
    const totalValue = calculateTotalValueWithSize(amount);
    const farmBoost = farmData?.farm_boost;
    const totalVolume =
      farmData && totalValue
        ? Number(farmData.trailing_volume || 0) + totalValue
        : totalValue;
    const farmBoostAfterTrade = calculateFarmBoostMultiplier(totalVolume);
    const boostedVolume = totalValue * farmBoostAfterTrade;
    const boostIncrease =
      farmBoost && totalValue && totalVolume
        ? calculateFarmBoostMultiplier(totalVolume) - Number(farmBoost)
        : 0;
    const boostIncreasePercentage =
      farmBoost && boostIncrease
        ? String((boostIncrease / Number(farmBoost)) * 100)
        : undefined;

    return [
      {
        key: "boosted_volume",
        title: (
          <BoostTitle>
            <div>{commonFormTranslations("boosted_volume")}</div>
            <BoostedVolumeTooltip
              tradeFormVolume={String(totalValue)}
              tradeFormBoostedVolume={String(boostedVolume)}
              boostIncrease={String(boostIncrease)}
              boostIncreasePercentage={boostIncreasePercentage}
              farmData={farmData}
            />
          </BoostTitle>
        ),
        value: (
          <BoostValueWrapper>
            {boostedVolume ? currency(boostedVolume).format() : "---"}
          </BoostValueWrapper>
        ),
      },
    ];
  }, [amount, calculateTotalValueWithSize, commonFormTranslations, farmData]);

  const verifyNotEnoughBalance = useCallback(
    (amt: string) => {
      // Spot: a BUY spends only the QUOTE balance, a SELL sells only the BASE
      // balance (never the portfolio total). `sizeCap` is spotOrderMax over
      // exactly those, exact and in the field's own unit. An UNKNOWN balance
      // passes here (it is not "insufficient"); the order is held instead by
      // `balanceUnknownReason`, which disables submit in the submit section —
      // do not remove that guard thinking this validator covers it.
      if (isSpot) return sizeWithinCap(amt, sizeCap);

      // The value being validated, not the last watched `amount`, which lags
      // one edit behind inside react-hook-form's validate call.
      const size = getRealContractSize(amt, tradeUseUSDCTerms);
      if (
        currentPosition &&
        currentPosition.side !== orderDirection &&
        Number(currentPosition.amount) >= size
      ) {
        return true;
      }
      // The P2 ceiling (usePerpsTradeFormMax): exact, flip-aware, capped by the
      // chain's maxOrderSize. handleSubmit re-runs this with the ceiling of the
      // moment, so it is also the submit guard — one rule, no drift.
      return sizeWithinCap(amt, sizeCap);
    },
    [
      sizeCap,
      currentPosition,
      getRealContractSize,
      tradeUseUSDCTerms,
      orderDirection,
      isSpot,
    ]
  );

  // The size entered cannot be checked against a balance the API has not given
  // (B-1): no "Insufficient" error, but submit waits. A perp size the opposite
  // position covers needs no balance, as in the validator above.
  const balanceUnknownReason = useMemo(() => {
    if (!isSpot && amount) {
      const size = getRealContractSize(amount, tradeUseUSDCTerms);
      if (
        currentPosition &&
        currentPosition.side !== orderDirection &&
        Number(currentPosition.amount) >= size
      ) {
        return undefined;
      }
    }
    return sizeBalanceUnknownReason(amount, sizeCap);
  }, [
    amount,
    currentPosition,
    getRealContractSize,
    isSpot,
    orderDirection,
    sizeCap,
    tradeUseUSDCTerms,
  ]);

  // 25 / 50 / 75 / MAX, the slider and a click on "Available to Trade" all
  // fill a share of `sizeMax`. A chosen share follows the TRADER's changes
  // (leverage, side, order type, unit) and never a book tick — see
  // usePerpSizePercent. Leverage itself is never written from here.
  const writeSize = useCallback(
    (text: string) =>
      setValue(PerpFormFieldKeyEnum.AMOUNT, text, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      }),
    [setValue]
  );
  const sizeControls = usePerpSizePercent({
    amount,
    max: sizeMax,
    step: sizeStep,
    followKey: `${selectedLeverage}|${orderDirection}|${orderType}|${tradeUseUSDCTerms}`,
    write: writeSize,
  });

  // The ceiling moves with the market (a book tick, a mark update). Re-check a
  // size already entered, so "exceeds max" appears when the market takes the
  // room away rather than only after the trader's next keystroke.
  useEffect(() => {
    if (amount) trigger(PerpFormFieldKeyEnum.AMOUNT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sizeCap]);

  // What an order-form TP/SL entered as ROE% is measured from: the limit price
  // for a limit order, the mark for a market one; this order's leverage.
  const tpslRoeRef = useMemo<TpslRoeRef>(
    () => ({
      side: orderDirection,
      entry: orderType === OrderTypeResponse.Limit ? price : markPrice,
      leverage: selectedLeverage,
      priceStep: contractPriceStep.price_step,
    }),
    [contractPriceStep.price_step, markPrice, orderDirection, orderType, price, selectedLeverage]
  );

  return {
    ...ctx,
    tpslRoeRef,
    tradeInfo,
    bottomTradeInfo,
    verifyNotEnoughBalance,
    balanceUnknownReason,
    sizePercent: sizeControls.sliderPercent,
    activeSizePercent: sizeControls.activePercent,
    setSizePercent: sizeControls.setPercent,
    setSizeMax: sizeControls.setMax,
  };
}
